import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "reactstrap";

const DISCLAIMER_TYPES: Record<string, string> = {
  broker: "brokerData",
  signal: "noAdvice",
  performance: "pastResults",
  full: "fullDisclaimer",
};

interface LegalDisclaimerProps {
  type?: "broker" | "signal" | "performance" | "full";
  children?: React.ReactNode;
  showIcon?: boolean;
  id?: string;
}

/**
 * LegalDisclaimer - Tooltip wrapper for MiFID II / CNMV compliance
 * Shows legal disclaimer when hovering over broker-sourced data
 */
const LegalDisclaimer: React.FC<LegalDisclaimerProps> = ({ type = "broker", children, showIcon = true, id }) => {
  const { t } = useTranslation("legal", { keyPrefix: "legalDisclaimers" });
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const generatedId = useRef(`legal-disclaimer-${Math.random().toString(36).slice(2, 11)}`);
  const tooltipId = id || generatedId.current;
  const disclaimerKey = DISCLAIMER_TYPES[type] || DISCLAIMER_TYPES.broker;

  return (
    <>
      <span
        id={tooltipId}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          cursor: "help",
        }}
      >
        {children}
        {showIcon && (
          <i
            className="fas fa-info-circle"
            style={{
              fontSize: "0.7rem",
              opacity: 0.5,
              marginLeft: "4px",
            }}
          />
        )}
      </span>
      <Tooltip
        placement="top"
        isOpen={tooltipOpen}
        target={tooltipId}
        toggle={() => setTooltipOpen(!tooltipOpen)}
        style={{
          maxWidth: "280px",
          textAlign: "left",
        }}
      >
        <div style={{ padding: "4px", fontSize: "0.8rem" }}>{t(disclaimerKey)}</div>
      </Tooltip>
    </>
  );
};

interface LegalFooterProps {
  type?: "broker" | "signal" | "performance" | "full";
  /**
   * Colour of the disclaimer text, not of the surface behind it. It was
   * previously named `variant`, which read as the surface and produced white
   * text on a white card.
   */
  textTone?: "dark" | "light";
}

/**
 * LegalFooter - Static footer disclaimer for cards
 * Shows a small disclaimer text at the bottom of cards
 */
export const LegalFooter: React.FC<LegalFooterProps> = ({ type = "broker", textTone = "dark" }) => {
  const { t } = useTranslation("legal", { keyPrefix: "legalDisclaimers" });
  const disclaimerKey = DISCLAIMER_TYPES[type] || DISCLAIMER_TYPES.broker;

  const isDark = textTone === "dark";

  return (
    <div
      style={{
        marginTop: "12px",
        paddingTop: "8px",
        borderTop: isDark ? "1px solid rgba(0,0,0,0.1)" : "1px solid rgba(255,255,255,0.1)",
        fontSize: "0.7rem",
        color: isDark ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.5)",
        display: "flex",
        alignItems: "flex-start",
        gap: "6px",
      }}
    >
      <i className="fas fa-info-circle" style={{ fontSize: "0.65rem", marginTop: "2px" }} aria-hidden="true" />
      <span>{t(disclaimerKey)}</span>
    </div>
  );
};

export default LegalDisclaimer;
